import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Layout } from "antd";
import "./App.css";
import "./components/Sidebar.css";
import "./components/layout.css";
import Login from "./components/Login";
import { SidebarProvider, useSidebar } from "./context/SidebarContext";
import Dashboard from "./components/dashboard/Dashboard";
import TablaNovedadesConPermisos from "./components/novedades/TablaNovedadesConPermisos";
import TablaNovedades from "./components/novedades/TablaNovedades";
import TablaUsuarios from "./components/usuarios/TablaUsuarios";
import TablaCampus from "./components/campus/TablaCampus";
import TablaTiposNovedades from "./components/tipos-novedades/TablaTiposNovedades";
import TablaDependencias from "./components/dependencias/TablaDependencias";
import TablaTiposContratos from "./components/tipos-contratos/TablaTiposContratos";
import TablaEmpleados from "./components/empleados/TablaEmpleados";
import ManualUsuario from "./components/ManualUsuario";
import PerfilUsuario from "./components/PerfilUsuario";
import NavBar from "./components/NavBar";
import SidebarComponent from "./components/SidebarComponent";
import useAuthStore from "./store/authStore";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ProtectedRoute, PermissionRoute } from "./components/ProtectedRoute";
import { ROLES } from "./hooks/usePermissions";

const { Content } = Layout;

function App() {
  const { initializeAuth, logout, isAuthenticated, loading, user } =
    useAuthStore();

  useEffect(() => {
    // Verificar autenticación al cargar la aplicación
    const initAuth = async () => {
      await initializeAuth();
    };
    initAuth();
  }, [initializeAuth]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  

  // Layout común para todas las rutas protegidas con sidebar
  const AppLayout = ({ children }) => {
    return (
      <SidebarProvider>
        <AppLayoutInner>{children}</AppLayoutInner>
      </SidebarProvider>
    );
  };

  // Layout interno que tiene acceso al contexto del sidebar
  const AppLayoutInner = ({ children }) => {
    const { collapsed } = useSidebar();

    return (
      <Layout
        style={{ minHeight: "100vh", display: "flex", flexDirection: "row" }}
      >
        <SidebarComponent />
        <Layout
          className={`main-layout ${
            collapsed ? "sidebar-collapsed" : "sidebar-open"
          }`}
          data-sidebar-state={collapsed ? "sidebar-collapsed" : "sidebar-open"}
        >
          <NavBar handlogout={handleLogout} />
          <Content className="site-content">{children}</Content>
        </Layout>
      </Layout>
    );
  };

  return (
    <BrowserRouter basename="/snad">
      {/* Configuración del ToastContainer */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated && user && user.rol_id ? (
              <Navigate to="/dashboard" />
            ) : (
              <Login />
            )
          }
        />

        {/* Ruta principal - redirigir a login si no está autenticado */}
        <Route
          path="/"
          element={
            isAuthenticated && user && user.rol_id ? (
              <Navigate to="/dashboard" />
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        {/* Dashboard - acceso para todos los usuarios autenticados */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <PermissionRoute
                roles={[
                  ROLES.ADMINISTRADOR,
                  ROLES.ADMINISTRATIVO,
                  ROLES.AUDITOR,
                  ROLES.COORDINADOR,
                ]}
              >
                <AppLayout>
                  <Dashboard />
                </AppLayout>
              </PermissionRoute>
            </ProtectedRoute>
          }
        />

        {/* Rutas principales con permisos */}
        <Route
          path="/novedades"
          element={
            <ProtectedRoute>
              <PermissionRoute
                roles={[
                  ROLES.ADMINISTRADOR,
                  ROLES.ADMINISTRATIVO,
                  ROLES.AUDITOR,
                  ROLES.COORDINADOR,
                ]}
              >
                <AppLayout>
                  <TablaNovedadesConPermisos />
                </AppLayout>
              </PermissionRoute>
            </ProtectedRoute>
          }
        />

        {/* Solo administradores */}
        <Route
          path="/usuarios"
          element={
            <ProtectedRoute>
              <PermissionRoute roles={[ROLES.ADMINISTRADOR]}>
                <AppLayout>
                  <TablaUsuarios />
                </AppLayout>
              </PermissionRoute>
            </ProtectedRoute>
          }
        />

        {/* Administradores y administrativos */}
        <Route
          path="/empleados"
          element={
            <ProtectedRoute>
              <PermissionRoute roles={[ROLES.ADMINISTRADOR]}>
                <AppLayout>
                  <TablaEmpleados />
                </AppLayout>
              </PermissionRoute>
            </ProtectedRoute>
          }
        />

        {/* Solo administradores - Campus */}
        <Route
          path="/campus"
          element={
            <ProtectedRoute>
              <PermissionRoute roles={[ROLES.ADMINISTRADOR]}>
                <AppLayout>
                  <TablaCampus />
                </AppLayout>
              </PermissionRoute>
            </ProtectedRoute>
          }
        />

        {/* Solo administradores - Tipos de Novedades */}
        <Route
          path="/tipos-novedades"
          element={
            <ProtectedRoute>
              <PermissionRoute roles={[ROLES.ADMINISTRADOR]}>
                <AppLayout>
                  <TablaTiposNovedades />
                </AppLayout>
              </PermissionRoute>
            </ProtectedRoute>
          }
        />

        {/* Solo administradores - Dependencias */}
        <Route
          path="/dependencias"
          element={
            <ProtectedRoute>
              <PermissionRoute roles={[ROLES.ADMINISTRADOR]}>
                <AppLayout>
                  <TablaDependencias />
                </AppLayout>
              </PermissionRoute>
            </ProtectedRoute>
          }
        />

        {/* Solo administradores - Tipos de Contratos */}
        <Route
          path="/tipos-contratos"
          element={
            <ProtectedRoute>
              <PermissionRoute roles={[ROLES.ADMINISTRADOR]}>
                <AppLayout>
                  <TablaTiposContratos />
                </AppLayout>
              </PermissionRoute>
            </ProtectedRoute>
          }
        />

        {/* Manual - acceso para todos */}
        <Route
          path="/manual"
          element={
            <ProtectedRoute>
              <PermissionRoute
                roles={[
                  ROLES.ADMINISTRADOR,
                  ROLES.ADMINISTRATIVO,
                  ROLES.AUDITOR,
                  ROLES.COORDINADOR,
                ]}
              >
                <AppLayout>
                  <ManualUsuario />
                </AppLayout>
              </PermissionRoute>
            </ProtectedRoute>
          }
        />

        {/* Perfil - acceso para todos los usuarios autenticados */}
        <Route
          path="/perfil"
          element={
            <ProtectedRoute>
              <PermissionRoute
                roles={[
                  ROLES.ADMINISTRADOR,
                  ROLES.ADMINISTRATIVO,
                  ROLES.AUDITOR,
                  ROLES.COORDINADOR,
                ]}
              >
                <AppLayout>
                  <PerfilUsuario />
                </AppLayout>
              </PermissionRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="*"
          element={
            isAuthenticated && user && user.rol_id ? (
              <Navigate to="/dashboard" />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
